import React, { useEffect, useState } from "react";
import { APIFieldType, FieldType } from "../../../services/helpers/types";
import Field from "../../molecules/Field/Field";
import { ReportBindingType } from "../Form/Form";
import { getFieldClassName } from "./helpers";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../redux/store/store";
import useIsLoading from "../../../services/hooks/useIsLoading";
import FieldsSkeleton from "./FieldsSkeleton";
import useIsEditMode from "../../../services/hooks/useEditMode";
import DraggableFieldsList from "../../atoms/DraggableFieldsList/DraggableFieldsList";
import Button from "../../atoms/Button/Button";
import { Add } from "@mui/icons-material";
import Drawer from "../../molecules/Drawer/Drawer";
import AddFieldForm from "../AddFieldForm/AddFieldForm";
import {
  getMaxPosition,
  sendToasterMessage,
} from "../../../services/helpers/commonHelpers";
import useEditDrawerState from "../../../services/hooks/useEditFieldState";
import { setIsEditDrawerOpen } from "../../../redux/reducers/appReducer";
import { updateField } from "../../../services/api/fields.api";
import { MAX_POSITION } from "../../../services/helpers/constants";
import { useTranslation } from "react-i18next";

interface FieldsProps {
  tabID: number;
  items: FieldType[];
  reportBinds: ReportBindingType[];
  onFieldChange: (field: FieldType, value?: string) => void;
  onNoteOptionChange: (field: FieldType, value?: string) => void;
  onFieldVisibilityChange: (field: FieldType, value: boolean) => void;
}

const Fields = (props: FieldsProps) => {
  const {
    items,
    reportBinds,
    tabID,
    onFieldChange,
    onNoteOptionChange,
    onFieldVisibilityChange,
  } = props;

  const { t } = useTranslation();

  const [currentItems, setCurrentItems] = useState<FieldType[]>(items);

  const isTabletView = window.innerWidth < 1440;

  useEffect(() => {
    setCurrentItems(items);
  }, [items]);

  const dispatch = useDispatch();
  const editDrawerState = useEditDrawerState();

  const { isFieldsLoading } = useIsLoading();
  const isInEditMode = useIsEditMode();

  const width = useSelector((state: RootState) => state.app.width);
  useEffect(() => {
    const fieldsWrapperElement = document.getElementById("dui-fields-wrapper");
    fieldsWrapperElement?.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, [items]);

  const getFieldValue = (item: FieldType) => {
    const fieldBind = reportBinds.find((bind) => {
      return bind.id === item.fieldID;
    });
    if (fieldBind !== undefined) {
      return fieldBind.value;
    }
    return undefined;
  };

  const getNoteOptionValue = (item: FieldType) => {
    const fieldBind = reportBinds.find((bind) => {
      return bind.id === item.fieldID;
    });
    if (fieldBind !== undefined) {
      return fieldBind.noteOptionValue;
    }
    return undefined;
  };

  const getFieldVisibility = (item: FieldType) => {
    const fieldBind = reportBinds.find((bind) => {
      return bind.id === item.fieldID;
    });
    if (fieldBind !== undefined) {
      return fieldBind.visible;
    }
    return true;
  };

  if (isFieldsLoading) {
    return <FieldsSkeleton />;
  }
  const handleCreateField = (fieldID: number, newField: APIFieldType) => {
    dispatch(setIsEditDrawerOpen({ isOpen: false, type: "add" }));
    sendToasterMessage(dispatch, t("createFieldMessage"));

    const filteredCurrentItems = currentItems.filter(
      (item) => item.position !== MAX_POSITION
    );

    const highestPositionInFields = getMaxPosition(filteredCurrentItems) + 1;

    const builtNewField: FieldType = {
      fieldID: fieldID,
      name: newField.name,
      type: newField.type,
      noteOption: newField.noteOption ? true : false,
      tabID: newField.tabID,
      position: highestPositionInFields,
      placeholder: "",
      defaultValue: newField.defaultValue ?? "",
      value: newField.defaultValue ?? "",
      unit: newField.unit,
      options: newField.options,
    };

    setCurrentItems([...currentItems, builtNewField]);
  };

  const handleDeleteField = (itemID: number) => {
    dispatch(setIsEditDrawerOpen({ isOpen: false, type: "add" }));

    setCurrentItems(currentItems.filter((item) => item.fieldID !== itemID));
  };

  const handleEditField = (itemID: number, newField: APIFieldType) => {
    dispatch(setIsEditDrawerOpen({ isOpen: false, type: "add" }));

    const currentPosition =
      currentItems.findIndex((item) => item.fieldID === itemID) + 1;

    const newItems = currentItems.map((item) => {
      if (item.fieldID === itemID) {
        return {
          fieldID: itemID,
          name: newField.name,
          type: newField.type,
          noteOption: newField.noteOption ? true : false,
          tabID: newField.tabID,
          position: currentPosition,
          placeholder: "",
          defaultValue: newField.defaultValue ?? "",
          value: newField.defaultValue ?? "",
          unit: newField.unit,
          options: newField.options,
        };
      }
      return item;
    });
    updateField(dispatch, {
      ...newField,
      position: currentPosition,
      defaultValue: newField.defaultValue ?? "",
    }).then((response) => {
      setCurrentItems(newItems);
      sendToasterMessage(dispatch, t("editDone"));
    });
  };

  const handlePositionChange = (newItems: FieldType[]) => {
    setCurrentItems(newItems);
  };

  return (
    <div className="dui-fields-wrapper" id="dui-fields-wrapper">
      {isInEditMode ? (
        <div className="dui-fields-edit-wrapper">
          <DraggableFieldsList
            fields={currentItems}
            onCreateField={handleCreateField}
            onDelete={handleDeleteField}
            onEditField={handleEditField}
            onPositionChange={handlePositionChange}
          />
          {tabID !== 0 && (
            <div
              className="dui-fields-edit-add-button-wrapper"
              onClick={() => {
                dispatch(setIsEditDrawerOpen({ isOpen: true, type: "add" }));
              }}
            >
              <Button
                icon={<Add />}
                type="primary"
                corners="circle"
                className="dui-fields-edit-add-button"
              />
            </div>
          )}
          <Drawer
            isOpen={
              editDrawerState.isOpen &&
              (editDrawerState.type === "add" ||
                editDrawerState.type === "addSelected")
            }
            onClose={() =>
              dispatch(setIsEditDrawerOpen({ isOpen: false, type: "add" }))
            }
            widthRefElID={isTabletView ? "dui-main-page" : "dui-doctors-report"}
          >
            <AddFieldForm
              tabID={tabID}
              position={
                getMaxPosition(
                  currentItems.filter(
                    (item) => typeof item.fieldID === "number"
                  )
                ) + 1
              }
              onCreateField={handleCreateField}
            />
          </Drawer>
        </div>
      ) : (
        items.map((item) => (
          <Field
            key={item.fieldID}
            field={item}
            onFieldChange={onFieldChange}
            value={getFieldValue(item)}
            noteOptionValue={getNoteOptionValue(item)}
            onNoteOptionChange={onNoteOptionChange}
            className={getFieldClassName(
              item.type,
              width < 1440,
              item.noteOption
            )}
            isVisible={getFieldVisibility(item)}
            onFieldVisibilityChange={onFieldVisibilityChange}
          />
        ))
      )}
    </div>
  );
};

export default Fields;
